import { AfterViewInit, Component, ElementRef } from '@angular/core';
import * as _ from 'lodash';
import { SelectionInputEntity, SelectionInputEntityValue, SelectionInputTreeEditModel } from '../../../../../pages/shared/core/Model/Trees/SelectionInputTree';

@Component({
    selector: 'app-selection-input-tree',
    template: `<div class="selection-input-tree"></div>`,
    standalone: false
})
export class SelectionInputTreeComponent implements AfterViewInit {
  set editData(val: any) {
    this._editData = val;
    this.refreshTree();
  }

  private _$tree: JQuery;
  private _editData: SelectionInputTreeEditModel;
  private _createdTreeBefore: boolean;

  get isValid(): boolean {
    return this._$tree?.find('.feature-tree-textbox-invalid').length <= 0;
  }

  get values(): SelectionInputEntityValue[] {
    if (!this._$tree || !this._createdTreeBefore) {
      return [];
    }

    const selectedFeatures = this._$tree.jstree('get_selected', true);
    return _.map(this._editData.entities, e => {
      let entityValue = new SelectionInputEntityValue();
      entityValue.name = e.name;

      if (!e.inputType || e.inputType.name === 'CHECKBOX') {
        entityValue.value = _.some(selectedFeatures, { original: { id: e.name } }) ? 'true' : 'false';
      } else {
        const result = this._editData.entityValues.find(ev => ev.name === entityValue.name);
        entityValue.value = result?.value ?? null;
      }

      return entityValue;
    });
  }

  constructor(private _element: ElementRef) {
    this._editData = { entities: [], entityValues: [] };
  }


  ngAfterViewInit(): void {
    this._$tree = $(this._element.nativeElement);
    this.refreshTree();
  }

  refreshTree(): void {
    let self = this;

    if (this._createdTreeBefore) {
      this._$tree.jstree('destroy');
    }

    this._createdTreeBefore = false;

    if (!this._editData || !this._$tree) {
      return;
    }

    let treeData = _.map(this._editData.entities, function (item) {
      return {
        id: item.name,
        parent: item.parentName ? item.parentName : '#',
        text: item.displayName,
        state: {
          opened: item.parentName ? false : true,
          selected: _.some(self._editData.entityValues, { name: item.name, value: 'true' })
        }
      };
    });

    this._$tree.jstree({
      'core': {
        data: treeData,
        themes: {
          responsive: false,
          icons: false
        }
      },
      'checkbox': {
        keep_selected_style: false,
        three_state: false,
        cascade: ''
      },
      plugins: ['checkbox', 'search']
    });

    this._createdTreeBefore = true;

    let inTreeChangeEvent = false;

    this._$tree.on('ready.jstree', function (e, data) {
      self.customizeNodes();
    });

    this._$tree.on('redraw.jstree', function (e, data) {
      self.customizeNodes();
    });

    this._$tree.on('after_open.jstree', function (e, data) {
      self.customizeNodes();
    });

    this._$tree.on('create_node.jstree', function (e, data) {
      self.customizeNodes();
    });

    this._$tree.on('changed.jstree', function (e, data) {
      if (!data.node) {
        return;
      }

      let wasInTreeChangeEvent = inTreeChangeEvent;
      if (!wasInTreeChangeEvent) {
        inTreeChangeEvent = true;
      }

      let childrenNodes;

      if (data.node.state.selected) {
        childrenNodes = $.makeArray(self._$tree.jstree('get_node', data.node).children);
        self._$tree.jstree('select_node', childrenNodes);

      } else {
        childrenNodes = $.makeArray(self._$tree.jstree('get_node', data.node).children);
        self._$tree.jstree('deselect_node', childrenNodes);
      }

      if (!wasInTreeChangeEvent) {
        inTreeChangeEvent = false;
      }
    });

    this._$tree.on('beforecollapse', function (node, event) {
      if (event.type == 'dblclick') { return false; }
    });
  }

  customizeNodes() {
    const self = this;

    this._$tree.find('.jstree-node').each(function () {
      const $node = $(this);
      const $anchorNode = $node.find('.jstree-anchor');

      const name = $node.attr('id') ?? '';
      const entity = self.findEntity(name);
      const entityValue = self.findEntityValue(name) || '';

      if (entity?.inputType === undefined || entity?.inputType === null) {
        return;
      }

      const inputType = entity.inputType;
      if (inputType.name === 'CHECKBOX') {
        if (inputType.attributes['disabled']) {
          self._$tree.jstree('disable_node', $node);
        }
      }
      else if (inputType.name === 'SINGLE_LINE_STRING') {
        if (!$node.find('.feature-tree-textbox').length) {
          $anchorNode.find('.jstree-checkbox').hide();

          let validator = inputType.validator;
          let type = validator?.name === 'NUMERIC' ? 'number' : 'text';

          const $textbox = $('<input type="' + type + '" class="feature-tree-textbox" />')
            .val(entityValue);

          if (type === 'number') {
            $textbox.attr('min', validator.minValue);
            $textbox.attr('max', validator.maxValue);
          } 
          else if (type === 'text') {
            self.validateLengthAndPattern(validator, $textbox);
          }

          $textbox.on('input propertychange paste', () => {
            const value = $textbox.val() as string;
            if (self.isEntityValueValid(name, value)) {
              self.setEntityValue(name, value);
              $textbox.removeClass('feature-tree-textbox-invalid');
              $textbox.css({ 'border': '' });

            } else {
              $textbox.addClass('feature-tree-textbox-invalid');
              $textbox.css({'border': '2px solid #f44336' });
            }
          });

          $textbox.appendTo($node);
        }
      }
    });
  }

  validateLengthAndPattern(validator: any, $textbox: JQuery<HTMLElement>) {
    if (validator.maxLength > 0) {
      $textbox.attr('maxlength', validator.maxLength);
    }

    if (validator.minLength > 0) {
      $textbox.attr('required', 'required');
    }

    if (validator.regularExpression) {
      $textbox.attr('pattern', validator.regularExpression);
    }
  }

  search(value: string) {
    let self = this;
    if (self._$tree) {
      self._$tree.jstree(true).search(value);
    }
  }

  private findEntity(name: string): SelectionInputEntity {
    const entity = this._editData.entities.find(e => e.name === name);
    if (!entity) {
      throw new Error('Could not find a entity ' + name)
    }

    return entity;
  }

  private findEntityValue(name: string): string {
    const entity = this.findEntity(name);
    if (!entity) {
      return '';
    }

    const entityValue = this._editData.entityValues.find(e => e.name === name);
    if (!entityValue) {
      return entity.defaultValue;
    }

    return entityValue.value ?? '';
  }



  private isEntityValueValid(name: string, value: string): boolean {
    const entity = this.findEntity(name);
    if (!entity?.inputType?.validator) {
      return true;
    }

    const validator = entity.inputType.validator;
    if (validator.name === 'STRING') {
      if (value === undefined || value === null || value.trim() === '') {
        return validator.allowNull;
      }

      if (typeof value !== 'string') {
        return false;
      }

      if (validator.minLength > 0 && value.length < validator.minLength) {
        return false;
      }

      if (validator.maxLength > 0 && value.length > validator.maxLength) {
        return false;
      }

      if (validator.regularExpression) {
        return (new RegExp(validator.regularExpression)).test(value);
      }
    }
    else if (validator.name === 'NUMERIC') {
      if (value === undefined || value === null || value.trim() === '')
        return false;

      const numericValue = +value;

      if(isNaN(numericValue)) {
        return false;
      }

      if (validator.minValue > numericValue) {
        return false;
      }

      if (validator.maxValue > 0 && numericValue > validator.maxValue) {
        return false;
      }
    }
    return true;
  }

  private setEntityValue(name: string, value: string) {
    const entityValue = this._editData.entityValues.find(e => e.name === name);
    if (!entityValue) {
      return;
    }

    entityValue.value = value;
  }
}
